import { split, ApolloClient, InMemoryCache, from, ApolloLink } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
// eslint-disable-next-line import/extensions
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { createClient } from 'graphql-ws';
import introspection from './apolloClient.introspection';
import runtime from './runtime';
import extractFromJWT from './utils/extractFromJWT';
import prepareDataForGraphQL from './utils/prepareDataForGraphQL';
import { getToken, onTokenUpdate, triggerUnauthenticatedError } from './utils/session';

/** A token past its own expiry is not worth a round trip, and never worth being rejected for. */
const isExpired = (token: string) => {
    try {
        return Date.now() >= extractFromJWT(token).exp * 1000;
    } catch {
        // `getToken` discards a token it cannot decode when the session bootstraps, so this is a
        // fallback rather than a path with a caller. It sends the token regardless: withholding it
        // on a parsing quirk would be a silent inability to authenticate, and the server verifies
        // it properly - answering anonymously rather than failing when it cannot use it.
        return false;
    }
};

// extract the host and protocol from the window location
const { host, protocol } = window.location;

// create the HTTP link for standard GraphQL queries/mutations
const httpLink = createUploadLink({ uri: `${protocol}//${host}/api/graphql`, credentials: 'include' });

// create a WebSocket link for Subscriptions
const wsProtocol = protocol === 'https:' ? 'wss' : 'ws';

const wsClient = createClient({
    url: `${wsProtocol}://${host}/api/subscriptions`,
    lazy: true,
});

const wsLink = new GraphQLWsLink(wsClient);

// link to add the JWT token in authorization header
const addAuthorizationHeaderLink = new ApolloLink((operation, forward) => {
    // @ts-ignore
    operation.setContext(({ headers }) => {
        const token = getToken();

        if (!token || isExpired(token)) {
            // deliberately not `getToken(true)`, which clears storage as it reads: that publishes a
            // token update, and the subscriber below resets the store - so every request made after
            // expiry would tear down the cache while that same request was still being prepared.
            // Dropping the stored token belongs to whoever owns the session, not to a header link.
            return { headers };
        }

        return {
            headers: {
                ...headers,
                Authorization: `Bearer ${token}`,
                'Apollo-Require-Preflight': 'true',
            },
        };
    });

    return forward(operation);
});

// create a split link to either use HTTTP or WS links based on the definition
const splitLink = split(
    ({ query }) => {
        const definition = getMainDefinition(query);

        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    from([addAuthorizationHeaderLink, httpLink])
);

// link to prepare GraphQL variables for submissions
const prepareDataLink = new ApolloLink((operation, forward) => {
    // eslint-disable-next-line no-param-reassign
    operation.variables = prepareDataForGraphQL(operation.variables);

    return forward(operation);
});

const isUnauthenticatedError = (errors: readonly { extensions?: Record<string, any> | null }[] | undefined) =>
    !!errors?.some(error => error.extensions?.code === 'UNAUTHENTICATED');

// handle unauthenticated errors
const handleUnauthenticatedErrorLink = onError(({ graphQLErrors, networkError }) => {
    if (isUnauthenticatedError(graphQLErrors)) {
        triggerUnauthenticatedError();

        return;
    }

    // a non 2xx response never populates `graphQLErrors` - the body arrives on the network error
    // instead - so checking only the former misses every auth failure the server answers with a
    // status of its own
    const body = (networkError as { result?: { errors?: { extensions?: Record<string, any> | null }[] } } | null)
        ?.result;

    if (isUnauthenticatedError(body?.errors)) {
        triggerUnauthenticatedError();
    }
});

// counterpart of `SESSION_INVALID_HEADER` in the backend's `serveApolloServer`
const SESSION_INVALID_HEADER = 'X-Session-Invalid';

/**
 * Drop a credential the server told us it cannot use.
 *
 * The server answers these requests normally - an anonymous caller gets a valid response - so
 * there is no error for the link above to match on. Without this the browser keeps presenting a
 * dead token on every request until something else clears it.
 */
const handleInvalidSessionLink = new ApolloLink((operation, forward) =>
    forward(operation).map(result => {
        const { response } = operation.getContext() as { response?: Response };

        if (response?.headers?.get(SESSION_INVALID_HEADER) && getToken()) {
            triggerUnauthenticatedError();
        }

        return result;
    })
);

const cache = new InMemoryCache({ possibleTypes: introspection.possibleTypes });

// initialize the apollo client
const client = new ApolloClient({
    link: from([prepareDataLink, handleUnauthenticatedErrorLink, handleInvalidSessionLink, splitLink]),
    cache,
    connectToDevTools: runtime.apolloDevToolsEnabled,
});

onTokenUpdate(token => {
    if (!token) {
        // once the client is signed out make sure we dropped everything
        // ensure nothing sensitive may remain in the cache
        client.clearStore();
    }
});

export default client;
